import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Card from "react-bootstrap/Card";
import ListGroup from "react-bootstrap/ListGroup";
import Modal from "react-bootstrap/Modal";
import { useForm } from "react-hook-form";
import SubsiteService from "../../services/subsite.service";
import UploadLogo from "../modals/UploadLogo";
import UserService from "../../services/user.service";
import ChooseSubsiteAddress from "./ChooseSubsiteAddress";
import { ArrowLeft, Trash } from 'react-bootstrap-icons';

const BACKEND_URL = process.env.REACT_APP_LOCALHOST_URL;

const SubsiteDetails = () => {
  const { subsiteId } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [allSubsites, setAllSubsites] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [shipToSelf, setShipToSelf] = useState(false);
  const [requireNewHireKit, setRequireNewHireKit] = useState(false);
  const [showFeaturedPage, setShowFeaturedPage] = useState(true);
  const [subsite, setSubsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [togglingActive, setTogglingActive] = useState(false);
  const [showActiveModal, setShowActiveModal] = useState(false);
  const [activeModalError, setActiveModalError] = useState('');

  const isNewSubsite = subsiteId === 'new';

  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      subsitename: '',
      code: '',
      poc: '',
      logo: '',
      paymenttype: '',
      newDayLimit: '',
      requireNewHireKit: false,
      newHireKitDeadlineDays: '30',
      id: null,
      copySubsiteId: null,
      copyLogos: false,
      company: '',
      shipToSelf: false,
      maxOrdersPerBalance: '',
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      if (isNewSubsite) {
        // Fetch all subsites for copy purposes
        const subsites = await SubsiteService.getAllSubsites();
        if (subsites && subsites.data.length > 0) {
          setAllSubsites(subsites.data);
        }
        setLoading(false);
      } else {
        // Fetch the specific subsite (works for active and inactive)
        const foundSubsite = await SubsiteService.getSubsiteById(subsiteId);

        if (foundSubsite) {
          setSubsite(foundSubsite);
          setShipToSelf(foundSubsite.shipToSelf);
          setRequireNewHireKit(!!foundSubsite.requireNewHireKit);
          setShowFeaturedPage(foundSubsite.showFeaturedPage !== false && foundSubsite.showFeaturedPage !== 0);

          // Reset form with subsite data
          reset({
            subsitename: foundSubsite.subsiteName,
            code: foundSubsite.abbreviation,
            poc: foundSubsite.internalPOCs,
            logo: foundSubsite.logoFilename,
            paymenttype: foundSubsite.paymentOptions,
            newDayLimit: "" + foundSubsite.newDayLimit,
            requireNewHireKit: !!foundSubsite.requireNewHireKit,
            newHireKitDeadlineDays: "" + (foundSubsite.newHireKitDeadlineDays || 30),
            id: foundSubsite.id,
            copySubsiteId: null,
            copyLogos: false,
            company: foundSubsite.company,
            shipToSelf: foundSubsite.shipToSelf,
            showFeaturedPage: foundSubsite.showFeaturedPage !== false && foundSubsite.showFeaturedPage !== 0,
            maxOrdersPerBalance: foundSubsite.maxOrdersPerBalance || '',
          });

          // Fetch admins
          const allAdmins = await UserService.getSubsiteAdmins(foundSubsite.id);
          setAdmins(allAdmins.data);

          // Fetch notes
          const notesResponse = await SubsiteService.getSubsiteNotes(foundSubsite.id);
          setNotes(notesResponse.data);
        }

        // Still need active subsites list for any copy helpers
        const subsites = await SubsiteService.getAllSubsites();
        if (subsites && subsites.data.length > 0) {
          setAllSubsites(subsites.data);
        }

        setLoading(false);
      }
    };

    fetchData();
  }, [subsiteId, isNewSubsite, reset]);

  const onSubmit = async (data) => {
    data.uploadedImage = [file];
    data.shipToSelf = shipToSelf;
    data.requireNewHireKit = requireNewHireKit;
    data.newHireKitDeadlineDays = requireNewHireKit ? data.newHireKitDeadlineDays : null;
    data.showFeaturedPage = showFeaturedPage;
    setSubmitting(true);

    try {
      await SubsiteService.addEditSubsite(data);
      navigate('/admin/subsites');
    } catch (error) {
      console.error("Error saving subsite:", error);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/subsites');
  };

  const handleAddNote = async () => {
    if (newNote.trim() === '') return;

    try {
      const response = await SubsiteService.addSubsiteNote(subsiteId, newNote);
      setNotes([response.data, ...notes]);
      setNewNote('');
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await SubsiteService.deleteSubsiteNote(noteId);
      setNotes(notes.filter(note => note.id !== noteId));
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openActiveModal = () => {
    if (!subsite) return;
    setActiveModalError('');
    setShowActiveModal(true);
  };

  const handleToggleActiveConfirm = async () => {
    if (!subsite) return;
    const nextActive = !subsite.active;

    setTogglingActive(true);
    setActiveModalError('');
    try {
      const updated = await SubsiteService.setSubsiteActiveById(subsite.id, nextActive);
      setSubsite({ ...subsite, active: updated.active });
      setShowActiveModal(false);
    } catch (error) {
      console.error("Error updating subsite status:", error);
      setActiveModalError("Failed to update subsite status. Please try again.");
    } finally {
      setTogglingActive(false);
    }
  };

  if (loading) {
    return (
      <div className="page_container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const pageTitle = isNewSubsite ? "Add Subsite" : "Edit Subsite";
  const submitButtonText = isNewSubsite ? "Add Subsite" : "Save Changes";

  return (
    <div className="page_container">
      <div style={{ marginBottom: 30 }}>
        <Button variant="link" onClick={handleCancel} style={{ paddingLeft: 0 }}>
          <ArrowLeft /> Back to Subsites
        </Button>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 30, gap: 12, flexWrap: 'wrap' }}>
          <h2 style={{ marginBottom: 0 }}>{pageTitle}</h2>
          {!isNewSubsite && subsite && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                padding: '4px 10px',
                borderRadius: 4,
                backgroundColor: subsite.active ? '#198754' : '#6c757d',
                color: '#fff',
                fontSize: 14
              }}>
                {subsite.active ? 'Active' : 'Inactive'}
              </span>
              <Button
                variant={subsite.active ? 'outline-secondary' : 'success'}
                disabled={togglingActive}
                onClick={openActiveModal}
              >
                {subsite.active ? 'Deactivate' : 'Activate'}
              </Button>
            </div>
          )}
        </div>

        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information Section */}
          <div style={{ backgroundColor: '#f8f9fa', padding: 20, borderRadius: 8, marginBottom: 20 }}>
            <h5 style={{ marginBottom: 20 }}>Basic Information</h5>

            <Form.Group className="mb-3" controlId="subsitename">
              <Form.Label>Name</Form.Label>
              <Form.Control
                {...register("subsitename", { required: true })}
                type="text"
                isInvalid={errors.subsitename}
              />
              {errors.subsitename && <Form.Control.Feedback type="invalid">Name is required</Form.Control.Feedback>}
            </Form.Group>

            <Form.Group className="mb-3" controlId="code">
              <Form.Label>Code</Form.Label>
              <Form.Control
                {...register("code", { required: true })}
                type="text"
                isInvalid={errors.code}
              />
              {errors.code && <Form.Control.Feedback type="invalid">Code is required</Form.Control.Feedback>}
            </Form.Group>

            <Form.Group className="mb-3" controlId="company">
              <Form.Label>Company</Form.Label>
              <Form.Control
                {...register("company", { required: true })}
                type="text"
                isInvalid={errors.company}
              />
              {errors.company && <Form.Control.Feedback type="invalid">Company is required</Form.Control.Feedback>}
            </Form.Group>

            <Form.Group className="mb-3" controlId="poc">
              <Form.Label>Point of Contact</Form.Label>
              <Form.Control
                {...register("poc", { required: true })}
                type="text"
                isInvalid={errors.poc}
              />
              {errors.poc && <Form.Control.Feedback type="invalid">Point of Contact is required</Form.Control.Feedback>}
            </Form.Group>

            <Form.Group className="mb-3" controlId="logo">
              <Form.Label>Logo</Form.Label>
              <UploadLogo
                register={register}
                fieldName="logo"
                currentLogo={subsite?.logoFilename}
                setFile={setFile}
              />
            </Form.Group>
          </div>

          {/* Payment & Settings Section */}
          <div style={{ backgroundColor: '#f8f9fa', padding: 20, borderRadius: 8, marginBottom: 20 }}>
            <h5 style={{ marginBottom: 20 }}>Payment & Settings</h5>

            <Form.Group className="mb-3" controlId="paymenttype">
              <Form.Label>Payment Type</Form.Label>
              <div style={{ paddingLeft: 10 }}>
                <Form.Check
                  {...register("paymenttype", { required: true })}
                  type="radio"
                  label="Credit Card Only"
                  value="creditcard"
                  name="paymenttype"
                />
                <Form.Check
                  {...register("paymenttype", { required: true })}
                  type="radio"
                  label="Balance Only"
                  value="balance"
                  name="paymenttype"
                />
                <Form.Check
                  {...register("paymenttype", { required: true })}
                  type="radio"
                  label="Balance then Credit Card"
                  value="balance creditcard"
                  name="paymenttype"
                />
                <Form.Check
                  {...register("paymenttype", { required: true })}
                  type="radio"
                  label="Item Limit"
                  value="itemlimit"
                  name="paymenttype"
                />
                <Form.Check
                  {...register("paymenttype", { required: true })}
                  type="radio"
                  label="Item Kit"
                  value="kit"
                  name="paymenttype"
                />
                <Form.Check
                  {...register("paymenttype", { required: true })}
                  type="radio"
                  label="Demo (checkout without payment, order deleted after email)"
                  value="demo"
                  name="paymenttype"
                />
              </div>
              {errors.paymenttype && <div className="error">Payment Type is required</div>}
            </Form.Group>

            <Form.Group className="mb-3" controlId="newDayLimit">
              <Form.Label>Items are Marked new for</Form.Label>
              <div style={{ paddingLeft: 10 }}>
                <Form.Check
                  {...register("newDayLimit", { required: true })}
                  type="radio"
                  label="30 Days"
                  value={30}
                  name="newDayLimit"
                />
                <Form.Check
                  {...register("newDayLimit", { required: true })}
                  type="radio"
                  label="60 Days"
                  value={60}
                  name="newDayLimit"
                />
                <Form.Check
                  {...register("newDayLimit", { required: true })}
                  type="radio"
                  label="90 Days"
                  value={90}
                  name="newDayLimit"
                />
              </div>
              {errors.newDayLimit && <div className="error">New Limit is required</div>}
            </Form.Group>

            <Form.Group className="mb-3" controlId="requireNewHireKit">
              <Form.Label>New Hire Kit Gate</Form.Label>
              <Form.Check
                type="switch"
                label="Require new hire kit for new users"
                checked={requireNewHireKit}
                onChange={(e) => setRequireNewHireKit(e.target.checked)}
              />
              <Form.Text className="text-muted">
                New users only see the new hire kit page until they place a kit order (no pricing). After the window expires without an order, the full catalog unlocks.
              </Form.Text>
            </Form.Group>

            {requireNewHireKit && (
              <Form.Group className="mb-3" controlId="newHireKitDeadlineDays">
                <Form.Label>Kit purchase window</Form.Label>
                <div style={{ paddingLeft: 10 }}>
                  <Form.Check
                    {...register("newHireKitDeadlineDays", { required: requireNewHireKit })}
                    type="radio"
                    label="30 Days"
                    value={30}
                    name="newHireKitDeadlineDays"
                  />
                  <Form.Check
                    {...register("newHireKitDeadlineDays", { required: requireNewHireKit })}
                    type="radio"
                    label="60 Days"
                    value={60}
                    name="newHireKitDeadlineDays"
                  />
                  <Form.Check
                    {...register("newHireKitDeadlineDays", { required: requireNewHireKit })}
                    type="radio"
                    label="90 Days"
                    value={90}
                    name="newHireKitDeadlineDays"
                  />
                </div>
              </Form.Group>
            )}

            <Form.Group className="mb-3" controlId="showFeaturedPage">
              <Form.Label>Storefront Home</Form.Label>
              <Form.Check
                type="switch"
                label="Show Featured page as home"
                checked={showFeaturedPage}
                onChange={(e) => setShowFeaturedPage(e.target.checked)}
              />
              <Form.Text className="text-muted">
                When off, users land on the first product category instead of Featured (for sites without featured items).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="maxOrdersPerBalance">
              <Form.Label>Max orders per balance (rollover)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="1"
                placeholder="Leave blank for unlimited"
                {...register("maxOrdersPerBalance")}
              />
              <Form.Text className="text-muted">
                Applies when a user has Balance Rolls Over enabled. Blank or 0 = unlimited orders until the balance is used up.
                Example: 3 lets them place up to 3 orders while they still have balance. Counter resets when their balance is re-allocated.
              </Form.Text>
            </Form.Group>
          </div>

          {/* Shipping & Addresses Section */}
          <div style={{ backgroundColor: '#f8f9fa', padding: 20, borderRadius: 8, marginBottom: 20 }}>
            <h5 style={{ marginBottom: 20 }}>Shipping & Addresses</h5>

            <Form.Group className="mb-3" controlId="shipToSelf">
              <Form.Check
                {...register("shipToSelf", { required: false })}
                type="switch"
                label="Users can ship anywhere"
                value={false}
                name="shipToSelf"
                onChange={(e) => setShipToSelf(e.target.checked)}
              />
            </Form.Group>

            {!shipToSelf && !isNewSubsite && (
              <div style={{ marginTop: 15 }}>
                <ChooseSubsiteAddress key={subsiteId} subsiteId={subsiteId} />
              </div>
            )}

            {!shipToSelf && isNewSubsite && (
              <div className="alert alert-info" style={{ marginTop: 15 }}>
                After adding this subsite, return here to add and edit addresses.
              </div>
            )}
          </div>

          {/* Admins Section - Only for existing subsites */}
          {!isNewSubsite && admins && admins.length > 0 && (
            <div style={{ backgroundColor: '#f8f9fa', padding: 20, borderRadius: 8, marginBottom: 20 }}>
              <h5 style={{ marginBottom: 20 }}>Subsite Admins</h5>
              <Form.Group className="mb-3" controlId="admin">
                <Form.Label>Admins who can Approve Orders</Form.Label>
                <div style={{ paddingLeft: 10 }}>
                  {admins.map((admin) => (
                    <Form.Check
                      key={admin.user.id}
                      {...register("approveAccess")}
                      type="checkbox"
                      label={admin.user.lastname + ", " + admin.user.firstname}
                      value={admin.user.id}
                      name="approveAccess"
                    />
                  ))}
                </div>
              </Form.Group>
            </div>
          )}

          {/* Copy Settings - Only for new subsites */}
          {allSubsites && allSubsites.length > 0 && isNewSubsite && (
            <div style={{ backgroundColor: '#f8f9fa', padding: 20, borderRadius: 8, marginBottom: 20 }}>
              <h5 style={{ marginBottom: 20 }}>Copy Settings (Optional)</h5>

              <Form.Group className="mb-3" controlId="copySubsiteId">
                <Form.Label>Copy from Existing Subsite</Form.Label>
                <Form.Select {...register("copySubsiteId")}>
                  <option value="">Select a subsite</option>
                  {allSubsites.map((subsite) => (
                    <option key={subsite.id} value={subsite.id}>{subsite.subsiteName}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" controlId="copyLogos">
                <Form.Check
                  {...register("copyLogos")}
                  type="checkbox"
                  label="Copy logos from selected subsite"
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="markNotNew">
                <Form.Check
                  {...register("markNotNew")}
                  type="checkbox"
                  label="Mark All Items Not New"
                />
              </Form.Group>
            </div>
          )}

          {/* Notes Section - Only for existing subsites */}
          {!isNewSubsite && (
            <div style={{ backgroundColor: '#f8f9fa', padding: 20, borderRadius: 8, marginBottom: 20 }}>
              <h5 style={{ marginBottom: 20 }}>Notes</h5>

              {/* Add Note Form */}
              <div style={{ marginBottom: 20 }}>
                <Form.Group controlId="newNote">
                  <Form.Label>Add a Note</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Enter a note about this subsite..."
                  />
                </Form.Group>
                <Button
                  variant="primary"
                  onClick={handleAddNote}
                  style={{ marginTop: 10 }}
                  disabled={newNote.trim() === ''}
                >
                  Add Note
                </Button>
              </div>

              {/* Notes List */}
              {notes.length > 0 ? (
                <ListGroup>
                  {notes.map((note) => (
                    <ListGroup.Item
                      key={note.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 10
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: 5 }}>
                          {formatDate(note.createdAt)}
                          {note.user && (
                            <span style={{ marginLeft: 10 }}>
                              by <strong>{note.user.firstname} {note.user.lastname}</strong>
                            </span>
                          )}
                          {!note.user && note.createdBy && (
                            <span style={{ marginLeft: 10 }}>
                              by <strong>{note.createdBy}</strong>
                            </span>
                          )}
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {note.note}
                        </div>
                      </div>
                      <Button
                        variant="link"
                        onClick={() => handleDeleteNote(note.id)}
                        style={{ color: '#dc3545', padding: 0, marginLeft: 10 }}
                      >
                        <Trash size={18} />
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="alert alert-info">
                  No notes yet. Add a note to keep track of important information about this subsite.
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            marginTop: 30,
            paddingTop: 20,
            borderTop: '1px solid #dee2e6'
          }}>
            <Button variant="secondary" onClick={handleCancel} size="lg">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting} size="lg">
              {isSubmitting ? 'Saving...' : submitButtonText}
            </Button>
          </div>
        </Form>
      </div>

      <Modal show={showActiveModal} onHide={() => !togglingActive && setShowActiveModal(false)} centered>
        <Modal.Header closeButton={!togglingActive}>
          <Modal.Title>{subsite?.active ? 'Deactivate Subsite' : 'Activate Subsite'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {subsite && (
            <p className="mb-0">
              Are you sure you want to {subsite.active ? 'deactivate' : 'activate'}{' '}
              <strong>{subsite.subsiteName}</strong>?
            </p>
          )}
          {activeModalError && (
            <div className="alert alert-danger mt-3 mb-0">{activeModalError}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowActiveModal(false)}
            disabled={togglingActive}
          >
            Cancel
          </Button>
          <Button
            variant={subsite?.active ? 'danger' : 'success'}
            onClick={handleToggleActiveConfirm}
            disabled={togglingActive}
          >
            {togglingActive ? 'Updating…' : (subsite?.active ? 'Deactivate' : 'Activate')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SubsiteDetails;